import React, {
  useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';

import getRadioShowsList from '../../../api/graphql/queries/getRadioShowsList';
import postDeleteRadioShow from '../../../api/graphql/mutations/postDeleteRadioShow';
import { STATUS_OPTIONS, CHANNEL_OPTIONS } from '../../../utils/radioShowForm';
import DataTable from '../../Table/DataTable';
import Filter from '../../Tabs/Filter';
import LoadIcon from '../../LoadIcon';
import RadioConfirmModal from './RadioConfirmModal';
import { programColumns, episodeColumns } from './radioColumns';
import { multilangLabel } from './RadioFields';

/*
The Programs or the Episodes list (`kind`): one server-side page at a time, with search,
status and channel filters, and for episodes the program filter. The query state is one
object so a filter change and the jump back to page 0 are a single fetch.
*/

const DEFAULT_ROWS_PER_PAGE = 50;
const SEARCH_DELAY_MS = 400;

const RadioShowsList = ({
  kind, programs, parentFilter, refreshKey, onEdit, onCreate, onShowEpisodes, onProgramsLoaded, onChanged, onAuthFail,
}) => {
  const [query, setQuery] = useState({
    search: '',
    status: '',
    channel: '',
    parent: kind === 'episodes' && parentFilter ? String(parentFilter) : '',
    page: 0,
    rowsPerPage: DEFAULT_ROWS_PER_PAGE,
    sorting: ['updatedAt', -1],
  });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteErrors, setDeleteErrors] = useState([]);
  const requestRef = useRef(0);
  const searchTimer = useRef(null);

  // The Programs list's episode count links here with a program preselected. The initial
  // value is already in the query state, so only a later change refilters.
  const appliedParentFilter = useRef(parentFilter);
  useEffect(() => {
    if (kind !== 'episodes' || appliedParentFilter.current === parentFilter) return;
    appliedParentFilter.current = parentFilter;
    setQuery(prev => ({ ...prev, parent: parentFilter ? String(parentFilter) : '', page: 0 }));
  }, [parentFilter]);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  useEffect(() => {
    requestRef.current += 1;
    const request = requestRef.current;
    setLoading(true);
    setLoadError('');
    getRadioShowsList({
      kind,
      search: query.search.trim() || undefined,
      status: query.status || undefined,
      channel: query.channel ? Number(query.channel) : undefined,
      parent: kind === 'episodes' && query.parent ? Number(query.parent) : undefined,
      limit: query.rowsPerPage,
      offset: query.page * query.rowsPerPage,
      sortField: query.sorting[0],
      sortOrder: query.sorting[1],
    }).then((result) => {
      if (request !== requestRef.current) return;
      setLoading(false);
      if (!result) {
        setLoadError('Could not load the list. Try again.');
        return;
      }
      if (!result.isAuth) {
        onAuthFail();
        return;
      }
      const lastPage = Math.max(0, Math.ceil((result.total || 0) / query.rowsPerPage) - 1);
      if (query.page > lastPage) {
        // The list shrank under this page (a delete, or a filter elsewhere): go to the
        // last page that still has rows instead of showing an empty table without paging.
        setQuery(prev => ({ ...prev, page: lastPage }));
        return;
      }
      setRows(result.shows || []);
      setTotal(result.total || 0);
      if (result.programs) onProgramsLoaded(result.programs);
    });
  }, [kind, query, refreshKey]);

  const setFilter = (name, value) => setQuery(prev => ({ ...prev, [name]: value, page: 0 }));

  const updateSearch = (value) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setFilter('search', value), SEARCH_DELAY_MS);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteErrors([]);
    const result = await postDeleteRadioShow({ id: deleteTarget.id });
    setDeleting(false);
    if (!result) {
      setDeleteErrors(['The server did not answer. Nothing was deleted.']);
      return;
    }
    if (!result.isAuth) {
      onAuthFail();
      return;
    }
    if (!result.ok) {
      const messages = (result.errors || []).map(error => error && error.message).filter(Boolean);
      setDeleteErrors(messages.length ? messages : ['It was not deleted.']);
      return;
    }
    const deleted = deleteTarget;
    setDeleteTarget(null);
    onChanged(`Deleted ${kind === 'programs' ? 'program' : 'episode'} #${deleted.id}.`, { kind });
  };

  const handlers = {
    onEdit: rowId => onEdit(kind, rowId),
    onDelete: (row) => {
      setDeleteErrors([]);
      setDeleteTarget(row);
    },
    onShowEpisodes,
  };
  const columns = kind === 'programs' ? programColumns(handlers) : episodeColumns(handlers);
  const programOptions = programs.map(program => ({
    value: String(program.id),
    label: `${multilangLabel(program.title, program.slug)} (#${program.id})`,
  }));
  const noun = kind === 'programs' ? 'program' : 'episode';
  const filtered = query.search || query.status || query.channel || query.parent;

  return (
    <div>
      <div className="flex justify-between mb-4">
        <span className="text-americaMonoBold text-26">{kind === 'programs' ? 'Programs' : 'Episodes'}</span>
        <button
          type="button"
          className="font-americaMonoBold text-xs pr-6 pl-8 py-2 rounded-full bg-black text-white uppercase h-10 flex items-center"
          onClick={() => onCreate(kind, kind === 'episodes' && query.parent ? query.parent : '')}
        >
          {`Create ${noun}`}
          <span className="font-icon icon-add-button text-white font-black pl-3 text-base" />
        </button>
      </div>
      <Filter
        updateSearch={updateSearch}
        placeholder={kind === 'programs' ? 'Search programs by title or slug' : 'Search episodes by title or slug'}
      />
      <div className="radio-filters">
        <label htmlFor={`radio-${kind}-status`}>
          Status
          <select id={`radio-${kind}-status`} value={query.status} onChange={event => setFilter('status', event.target.value)}>
            <option value="">Any</option>
            {STATUS_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label htmlFor={`radio-${kind}-channel`}>
          Channel
          <select id={`radio-${kind}-channel`} value={query.channel} onChange={event => setFilter('channel', event.target.value)}>
            <option value="">Any</option>
            {CHANNEL_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        {kind === 'episodes' ? (
          <div className="radio-filter-program">
            Program
            <ReactSelect
              inputId="radio-episodes-program"
              aria-label="Program"
              className="mt-1"
              isClearable
              placeholder="Any program"
              options={programOptions}
              value={programOptions.find(option => option.value === query.parent) || null}
              onChange={option => setFilter('parent', option ? option.value : '')}
            />
          </div>
        ) : null}
      </div>
      {loading ? <LoadIcon /> : null}
      {!loading && loadError ? <p className="radio-error-text">{loadError}</p> : null}
      {!loading && !loadError && rows.length ? (
        <DataTable
          tableData={rows}
          columns={columns}
          total={total}
          page={query.page}
          setPage={page => setQuery(prev => ({ ...prev, page }))}
          rowsPerPage={query.rowsPerPage}
          setRowsPerPage={rowsPerPage => setQuery(prev => ({ ...prev, rowsPerPage, page: 0 }))}
          sorting={query.sorting}
          setSorting={sorting => setQuery(prev => ({ ...prev, sorting, page: 0 }))}
        />
      ) : null}
      {!loading && !loadError && !rows.length ? (
        <p className="radio-empty">{filtered ? `No ${noun}s match these filters.` : `No ${noun}s yet.`}</p>
      ) : null}
      {deleteTarget ? (
        <RadioConfirmModal
          title={`Delete ${noun} #${deleteTarget.id}`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          busy={deleting}
          errors={deleteErrors}
        >
          <p>{`Delete “${multilangLabel(deleteTarget.title, deleteTarget.slug)}”?`}</p>
          {kind === 'programs' && deleteTarget.episodeCount ? (
            <p>{`It has ${deleteTarget.episodeCount} episode(s), so it cannot be deleted until they are deleted or moved to another program.`}</p>
          ) : null}
          {kind === 'episodes' ? <p>It disappears from the website, the schedule and the podcast feed.</p> : null}
        </RadioConfirmModal>
      ) : null}
    </div>
  );
};

RadioShowsList.propTypes = {
  kind: PropTypes.oneOf(['programs', 'episodes']).isRequired,
  programs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    slug: PropTypes.string,
    title: PropTypes.shape({}),
  })),
  // Episodes only: the program to filter on when the list opens.
  parentFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Bumped by the container after a save or delete elsewhere, to refetch.
  refreshKey: PropTypes.number,
  onEdit: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onShowEpisodes: PropTypes.func,
  onProgramsLoaded: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
  onAuthFail: PropTypes.func.isRequired,
};

RadioShowsList.defaultProps = {
  programs: [],
  parentFilter: '',
  refreshKey: 0,
  onShowEpisodes: () => {},
};

export default RadioShowsList;
